import { useEffect, useId, useMemo, useReducer, useRef, useState } from 'react'
import { autocompleteClasses } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { type FilterBy, type FilterTemplate } from './FilterTemplate'
import CircularProgress from '@mui/material/CircularProgress'
import Popper from '@mui/material/Popper'
import useAutocomplete from '@mui/material/useAutocomplete'
import Chip from '@mui/material/Chip'
import { omit } from 'common/api/v1/helpers'
import { alpha, styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Close from '@mui/icons-material/Close'
import Search from '@mui/icons-material/Search'
import Paper from '@mui/material/Paper'

import { distinct } from 'common/utils'

export interface FilteredSearchBarProps {
    // Filters that require a search token/template + operator, e.g. searchToken=name, operator='matches'
    searchTokenFilters: FilterTemplate[]

    // The filter to apply to raw text searches.
    // A raw text search is a "quick search" that doesn't require selecting a template, operator and value
    // but rather simply enter a string and press enter to search immediately.
    rawTextFilter?: FilterTemplate
    ariaLabel?: string
    formatError?: (error: unknown) => string

    appliedFilters: FilterBy[]
    onFiltersApplied: (filters: FilterBy[]) => void
}

interface FilterValue {
    value: string
    displayName: string
}
interface WipFilter {
    key: string
    operator?: FilterBy['operator']
    value?: FilterValue
}
type SavedFilter = Required<WipFilter>

interface CommonState {
    autocomplete: {
        placeholder: string
        // The text displayed in the search input text field
        searchText: string
        // Whether to allow the user entering their own custom values (i.e. don't constrain the options to the possibleValues).
        // e.g. when constructing a filter using the matches operator, e.g. "<key> matches <custom input>"
        allowCustomOptions: boolean
        // If the options popup should automatically open. If false the user must manually click to open it.
        autoOpenOptionsPopup: boolean
        // If the first available option in the list should automatically receive focus (convenient when navigating using keyboard)
        autoFocusFirstOption: boolean
    }
}

interface SelectTemplateState extends CommonState {
    state: 'selectTemplate'
}
interface SelectOperatorState extends CommonState {
    state: 'selectOperator'
    template: FilterTemplate
}
interface SelectValueState extends CommonState {
    state: 'selectValue'
    template: FilterTemplate
    operator: FilterBy['operator']
}

type State = SelectTemplateState | SelectOperatorState | SelectValueState

const selectTemplateState = (): SelectTemplateState => {
    return {
        state: 'selectTemplate',
        autocomplete: {
            placeholder: autocompletePlaceholder({
                state: 'selectTemplate',
            }),
            searchText: '',
            allowCustomOptions: false,
            autoOpenOptionsPopup: false, // Close popup after a filter is applied
            autoFocusFirstOption: true,
        },
    }
}

const selectOperatorState = (template: FilterTemplate): SelectOperatorState => ({
    state: 'selectOperator',
    template,
    autocomplete: {
        placeholder: autocompletePlaceholder({ state: 'selectOperator' }),
        searchText: '', // Reset search text
        allowCustomOptions: false, // Only allow user to select operator from list of predefined filters
        autoOpenOptionsPopup: true,
        autoFocusFirstOption: true,
    },
})

const selectValueState = (template: FilterTemplate, operator: SelectValueState['operator']): SelectValueState => {
    return {
        state: 'selectValue',
        template,
        operator,
        autocomplete: {
            placeholder: autocompletePlaceholder({ state: 'selectValue', template }),
            searchText: '', // Reset search text
            allowCustomOptions: filterRequiresCustomInput(template), // Allow user to enter custom value if there are no predefined values to select from
            autoOpenOptionsPopup: true,
            autoFocusFirstOption: true,
        },
    }
}

function reducer(
    prevState: State,
    action:
        | { type: 'wipFilterTemplateSelected'; template: FilterTemplate }
        | { type: 'wipFilterOperatorSelected'; template: FilterTemplate }
        | { type: 'wipFilterValueSelected' }
        | { type: 'wipFilterRemoved' }
        | { type: 'searchTextEntered'; text: string }
        | { type: 'filtersApplied'; filters: SavedFilter[] }
): State {
    switch (action.type) {
        case 'wipFilterTemplateSelected': {
            if (prevState.state !== 'selectTemplate') {
                throw new Error(
                    `Invalid state transition to selectOperator from ${prevState.state} (expected selectTemplate)`
                )
            }
            return selectOperatorState(action.template)
        }
        case 'wipFilterOperatorSelected': {
            if (prevState.state !== 'selectOperator') {
                throw new Error(
                    `Invalid state transition to selectValue from ${prevState.state} (expected selectOperator)`
                )
            }
            return selectValueState(action.template, action.template.operator)
        }
        case 'wipFilterValueSelected': {
            if (prevState.state !== 'selectValue') {
                throw new Error(
                    `Invalid state transition to selectTemplate from ${prevState.state} (expected selectValue)`
                )
            }
            return selectTemplateState()
        }
        case 'wipFilterRemoved': {
            return selectTemplateState()
        }
        case 'filtersApplied': {
            const isConstructingFilter = prevState.state !== 'selectTemplate'
            return {
                ...prevState,
                autocomplete: {
                    ...prevState.autocomplete,
                    searchText: '', // Reset search text on apply
                    placeholder: autocompletePlaceholder({
                        state: prevState.state,
                        template: 'template' in prevState ? prevState.template : undefined,
                    }),
                    // Keep options open if we're currently working on a filter
                    // e.g. user has selected template + operator and then clicks "X" on a previously applied filter
                    autoOpenOptionsPopup: isConstructingFilter,
                },
            }
        }
        case 'searchTextEntered': {
            return { ...prevState, autocomplete: { ...prevState.autocomplete, searchText: action.text } }
        }
    }
}

const defaultFormatError = (_error: unknown) => 'Unable to load filter options'

export const FilteredSearchBar = ({
    appliedFilters: currentFilters,
    searchTokenFilters,
    rawTextFilter,
    onFiltersApplied,
    ariaLabel = 'Search and filter',
    formatError = defaultFormatError,
}: FilteredSearchBarProps) => {
    const appliedFilters = useMemo(() => currentFilters.map(toSavedFilter).flat(), [JSON.stringify(currentFilters)])
    const [state, dispatch] = useReducer(reducer, selectTemplateState())

    useEffect(() => {
        dispatch({ type: 'filtersApplied', filters: appliedFilters })
    }, [JSON.stringify(appliedFilters)])

    const requestApplyFilters = (reason: string) => {
        if (isOptionsPopupOpen) return
        if (isPerformingRawTextSearch && rawTextFilter) {
            // Allow typing a custom string and pressing enter to search immediately by value
            // i.e. not having to select template, operator and value
            doApplyFilters(
                [
                    // Remove existing rawTextFilter since it will be re-inserted
                    ...appliedFilters.filter(
                        (f) => !(f.key === rawTextFilter.key && f.operator === rawTextFilter.operator)
                    ),
                    {
                        key: rawTextFilter.key,
                        operator: rawTextFilter.operator,
                        value: {
                            value: state.autocomplete.searchText,
                            displayName: state.autocomplete.searchText,
                        },
                    },
                ],
                `${reason} (raw text search)`
            )
            // Clear searchText immediately to ensure the input is cleared even if filters don't change
            // (e.g., when entering the same free text filter that already exists)
            dispatch({ type: 'searchTextEntered', text: '' })
        } else {
            // Allow pressing enter to search when options popup is closed
            doApplyFilters(appliedFilters, reason)
        }
    }
    const doApplyFilters = (filters: SavedFilter[], _reason: string) => {
        const distinctKeepingLastOccurrence = distinct(filters.toReversed(), (a, b) => {
            const canFilterOccurMultipleTimes = a.operator === 'isOneOf'
            return (
                a.key === b.key &&
                a.operator === b.operator &&
                (!canFilterOccurMultipleTimes || a.value?.value === b.value?.value)
            )
        }).toReversed()
        onFiltersApplied(toFilterBy(distinctKeepingLastOccurrence))
    }

    const [fetchState, setFetchState] = useState<{
        isLoading: boolean
        result: FilterValue[]
        error: string | undefined
    }>({
        result: [],
        isLoading: false,
        error: undefined,
    })

    const hasEnteredText = state.autocomplete.searchText.length > 0
    const isPerformingRawTextSearch = !!rawTextFilter && state.state === 'selectTemplate' && hasEnteredText
    const activeKey = 'template' in state ? state.template.key : undefined
    const activeOperator = 'operator' in state ? state.operator : undefined
    const activeTemplate = searchTokenFilters.find(
        (template) => template.key === activeKey && (!activeOperator || template.operator === activeOperator)
    )
    const possibleValues = activeTemplate?.possibleValues
    const possibleValuesSync = activeTemplate?.possibleValuesSync
    const hasAsyncOptions = !!possibleValues
    const optionCallbacks = useRef({ possibleValues, formatError })
    // Live list updates recreate callbacks without changing the option query.
    useEffect(() => {
        optionCallbacks.current = { possibleValues, formatError }
    }, [possibleValues, formatError])
    const templateOptionsKey = JSON.stringify(searchTokenFilters.map(({ key, operator }) => ({ key, operator })))
    const templateOptions: Pick<FilterTemplate, 'key' | 'operator'>[] = useMemo(
        () => JSON.parse(templateOptionsKey),
        [templateOptionsKey]
    )
    const [validationError, setValidationError] = useState<string>()
    const validationErrorId = useId()

    useEffect(() => setValidationError(undefined), [state.state, activeKey, activeOperator])

    useEffect(() => {
        let active = true
        let timer: ReturnType<typeof setTimeout> | undefined
        const showOptions = (options: FilterValue[]) => {
            if (!active) return
            setFetchState({
                isLoading: false,
                error: undefined,
                result: options.filter(
                    (option) =>
                        !appliedFilters.some(
                            (filter) =>
                                filter.key === activeKey &&
                                filter.operator === activeOperator &&
                                filter.value.value === option.value
                        )
                ),
            })
        }
        const showError = (error: unknown) => {
            if (active)
                setFetchState({ isLoading: false, error: optionCallbacks.current.formatError(error), result: [] })
        }
        if (isPerformingRawTextSearch) {
            setFetchState({ isLoading: false, error: undefined, result: [] })
        } else if (state.state === 'selectTemplate') {
            showOptions(
                [...new Set(templateOptions.map(({ key }) => key))].map((key) => ({ value: key, displayName: key }))
            )
        } else if (state.state === 'selectOperator') {
            showOptions(
                templateOptions
                    .filter(({ key }) => key === activeKey)
                    .map(({ operator }) => ({
                        value: operator,
                        displayName: formattedOperator(operator),
                    }))
            )
        } else if (possibleValuesSync) {
            try {
                showOptions(possibleValuesSync(state.autocomplete.searchText))
            } catch (error) {
                showError(error)
            }
        } else if (hasAsyncOptions) {
            setFetchState({ isLoading: true, error: undefined, result: [] })
            timer = setTimeout(() => {
                void Promise.resolve()
                    .then(() => optionCallbacks.current.possibleValues?.(state.autocomplete.searchText) ?? [])
                    .then(showOptions, showError)
            }, 500)
        } else {
            showOptions([])
        }
        return () => {
            active = false
            clearTimeout(timer)
        }
    }, [
        state.state,
        state.autocomplete.searchText,
        templateOptions,
        activeKey,
        activeOperator,
        hasAsyncOptions,
        possibleValuesSync,
        appliedFilters,
        isPerformingRawTextSearch,
    ])

    useEffect(() => {
        if (activeKey && !activeTemplate) dispatch({ type: 'wipFilterRemoved' })
    }, [activeKey, activeTemplate])

    const selectedFilterHasSelectableOptions = !state.autocomplete.allowCustomOptions
    const canOptionsPopupOpen = !isPerformingRawTextSearch && selectedFilterHasSelectableOptions

    const [wantsOptionPopupOpen, setWantsOptionPopupOpen] = useState(false)
    const isOptionsPopupOpen = canOptionsPopupOpen && wantsOptionPopupOpen
    useEffect(
        () => {
            // Update wantsOptionPopupOpen accordingly after a state change, i.e. let the new state decide if the popup should be opened by default or not.
            if (state.autocomplete.autoOpenOptionsPopup !== wantsOptionPopupOpen) {
                setWantsOptionPopupOpen(state.autocomplete.autoOpenOptionsPopup)
            }
        },
        // don't trigger effect on changes to 'wantsOptionPopupOpen' since then we can't toggle it (i.e. only observe state.autcomplete)
        [state.autocomplete]
    )

    const {
        getRootProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        focused: isAutocompleteFocused,
        groupedOptions: autocompleteOptions,
        setAnchorEl: setOptionsPopupAnchor,
        anchorEl: optionsPopupAnchor,
    } = useAutocomplete({
        open: isOptionsPopupOpen,
        openOnFocus: false, // false because we want explicit control of when the popup should open
        disableCloseOnSelect: true, // Token state keeps the next step open and closes after a completed value.
        onOpen: () => setWantsOptionPopupOpen(true),
        onClose: () => setWantsOptionPopupOpen(false),
        options: fetchState.result,
        getOptionLabel: (o) => {
            const isCustomOption = typeof o === 'string'
            return isCustomOption ? o : o.displayName
        },
        getOptionKey: (option) => (typeof option === 'string' ? option : option.value),
        freeSolo: state.autocomplete.allowCustomOptions,
        autoSelect: state.autocomplete.allowCustomOptions,
        autoHighlight: state.autocomplete.autoFocusFirstOption,
        inputValue: state.autocomplete.searchText,
        value: null, // value is never used but we set it to null to make the autocomplete controlled (and also to avoid the warnings about value not one of the available options)
        isOptionEqualToValue: (option, value) => typeof value !== 'string' && option.value === value.value,
        onInputChange: (_event, enteredText, reason) => {
            // Don't update `enteredText` when reason == 'reset' since it then contains
            // the **formatted** name of the selected value, not the actual selected value.
            // E.g. "xxx (appliance software is out of date)" instead of simply "xxx".
            if (['input', 'clear'].includes(reason)) {
                setValidationError(undefined)
                dispatch({ type: 'searchTextEntered', text: enteredText })
            }
        },
        onChange: (_event, selectedOption) => {
            // A value/option was selected from the popup
            if (!selectedOption) return
            if (state.state === 'selectTemplate') {
                if (typeof selectedOption === 'string') {
                    // Programmer error - should not happen (state.autocomplete.allowCustomOptions is false)
                    throw new Error(`Selected option has unexpected type`)
                }
                dispatch({
                    type: 'wipFilterTemplateSelected',
                    template: searchTokenFilters.find((e) => e.key === selectedOption.value)!,
                })
            } else if (state.state === 'selectOperator') {
                if (typeof selectedOption === 'string') {
                    // Programmer error - should not happen (state.autocomplete.allowCustomOptions is false)
                    throw new Error(`Selected option has unexpected type`)
                }
                const template = searchTokenFilters.find(
                    (filter) => filter.key === state.template.key && filter.operator === selectedOption.value
                )
                if (template) dispatch({ type: 'wipFilterOperatorSelected', template })
            } else if (state.state === 'selectValue') {
                const value =
                    typeof selectedOption === 'string'
                        ? { value: selectedOption, displayName: selectedOption }
                        : selectedOption
                let parsed = value
                try {
                    parsed = activeTemplate?.parseValue?.(value.value) ?? value
                    setValidationError(undefined)
                } catch (error) {
                    setValidationError(error instanceof Error ? error.message : 'Invalid filter value')
                    return
                }
                dispatch({ type: 'wipFilterValueSelected' })
                doApplyFilters(
                    [...appliedFilters, { key: state.template.key, operator: state.operator, value: parsed }],
                    'wipFilterValueSelected'
                )
            }
        },
    })

    const inputProps = getInputProps()

    const scrollInputIntoHorizontalView = () => {
        if (inputProps.ref && 'current' in inputProps.ref && inputProps.ref.current) {
            const element = inputProps.ref.current
            const elementRect = element.getBoundingClientRect()
            const windowHeight = window.innerHeight || document.documentElement.clientHeight
            const isVerticallyInView = elementRect.bottom > 0 && elementRect.top < windowHeight
            if (!isVerticallyInView) {
                return // Don't scroll horizontally if input isn't visible vertically
            }
            const parent = element.parentElement
            if (parent) {
                const parentRect = parent.getBoundingClientRect()
                if (elementRect.right > parentRect.right) {
                    // Element is cut off on the right, scroll right
                    parent.scrollLeft += elementRect.right - parentRect.right
                } else if (elementRect.left < parentRect.left) {
                    // Element is cut off on the left, scroll left
                    parent.scrollLeft -= parentRect.left - elementRect.left
                }
            }
        }
    }

    useEffect(() => {
        // Scroll input into view once on initial render (when autocomplete is not necessarily focused)
        scrollInputIntoHorizontalView()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isAutocompleteFocused) return
        if (appliedFilters.length === 0) return
        // Keep input field in view when many filters are selected and horizontal scrollbar is showing
        scrollInputIntoHorizontalView()
    }, [isAutocompleteFocused, appliedFilters, inputProps])

    return (
        <Root {...getRootProps()} data-testid="filteredSearch-root">
            <Box data-testid={'filteredSearch-wrapper1'}>
                <Box
                    data-testid={'filteredSearch-wrapper2'}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        data-testid={'filteredSearch-inputWrapper'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            overflowX: 'auto',
                            flexGrow: 1,
                            minWidth: 0,
                        }}
                    >
                        {appliedFilters.map((filter, index) => {
                            return (
                                <FilterView
                                    key={`${filter.key}${filter.operator}${filter.value.value}`}
                                    onDelete={() => doApplyFilters(appliedFilters.toSpliced(index, 1), 'delete chip')}
                                    filter={filter}
                                    dataTestId={`filteredSearch-appliedFilter-${filter.key}`}
                                />
                            )
                        })}
                        {'template' in state && (
                            <FilterView
                                filter={{
                                    key: state.template.key,
                                    operator: 'operator' in state ? state.operator : undefined,
                                }}
                                onDelete={() => dispatch({ type: 'wipFilterRemoved' })}
                                dataTestId={`filteredSearch-wipFilter-${state.template.key}`}
                            />
                        )}
                        <div
                            data-testid="filteredSearch-optionsPopupAnchor"
                            ref={setOptionsPopupAnchor}
                            style={{ width: 1 }}
                        />
                        <Input
                            autoFocus={false}
                            data-testid={'filteredSearch-input'}
                            {...omit(inputProps, 'value')}
                            value={state.autocomplete.searchText}
                            aria-label={ariaLabel}
                            aria-invalid={!!validationError}
                            aria-describedby={validationError ? validationErrorId : undefined}
                            placeholder={state.autocomplete.placeholder}
                            onKeyDown={(event) => {
                                // Convenient keyboard shortcuts
                                if (event.key === 'Enter') {
                                    if (state.state === 'selectTemplate') {
                                        requestApplyFilters('enter key pressed')
                                    }
                                    event.preventDefault()
                                } else if (event.key === 'Backspace') {
                                    if (hasEnteredText) {
                                        // Erase text only (i.e. don't perform any quick-actions).
                                        return
                                    }
                                    if (state.state === 'selectTemplate') {
                                        if (appliedFilters.length > 0) {
                                            // Allow pressing backspace to remove last applied filter and search
                                            doApplyFilters(
                                                appliedFilters.toSpliced(appliedFilters.length - 1, 1),
                                                'selectedTemplate::backspace'
                                            )
                                        }
                                    } else {
                                        // Allow pressing backspace to remove current wip-filter
                                        dispatch({ type: 'wipFilterRemoved' })
                                    }
                                }
                            }}
                        />
                    </Box>

                    <IconButton
                        aria-label="Apply search"
                        disabled={isOptionsPopupOpen || (!rawTextFilter && state.state === 'selectTemplate')}
                        onClick={() => {
                            requestApplyFilters('search button clicked')
                        }}
                    >
                        <Search />
                    </IconButton>
                    <IconButton
                        aria-label="Clear filters"
                        data-testid={'filteredSearch-clear'}
                        disabled={appliedFilters.length === 0}
                        onClick={() => {
                            if (appliedFilters.length > 0) {
                                doApplyFilters([], 'clear button clicked')
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>
            </Box>

            <Popper
                open={isOptionsPopupOpen || !!validationError}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
                anchorEl={optionsPopupAnchor}
                placement="bottom-start"
                modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]} // offset options y=12px below anchorEl
            >
                <Paper>
                    {validationError ? (
                        <Typography id={validationErrorId} role="alert" color="error" sx={{ px: 2, py: 1 }}>
                            {validationError}
                        </Typography>
                    ) : (
                        <OptionsContainer {...getListboxProps()} data-testid={'filteredSearchOptionsContainer-2'}>
                            {fetchState.isLoading && (
                                <li>
                                    <CircularProgress color="inherit" size={25} />
                                </li>
                            )}
                            {fetchState.error && (
                                <li>
                                    <Typography component="span" variant="body2" color="error">
                                        {fetchState.error}
                                    </Typography>
                                </li>
                            )}
                            {!fetchState.isLoading && !fetchState.error && autocompleteOptions.length === 0 && (
                                <li>
                                    <Typography component="span" variant="body1" color="textPrimary">
                                        No available options
                                    </Typography>
                                </li>
                            )}
                            {!fetchState.isLoading &&
                                !fetchState.error &&
                                autocompleteOptions.length > 0 &&
                                (autocompleteOptions as typeof fetchState.result).map((option, index) => {
                                    const { key, ...optionProps } = getOptionProps({ option, index })
                                    return (
                                        <li key={key} {...optionProps}>
                                            <Typography component="span" variant="body1" color="textPrimary">
                                                {option.displayName}
                                            </Typography>
                                        </li>
                                    )
                                })}
                        </OptionsContainer>
                    )}
                </Paper>
            </Popper>
        </Root>
    )
}

function autocompletePlaceholder({ state, template }: { state: State['state']; template?: FilterTemplate }) {
    switch (state) {
        case 'selectTemplate':
            return 'Search...'
        case 'selectOperator':
            return 'select operator'
        case 'selectValue': {
            return selectValuePlaceholder(template)
        }
    }
}

function selectValuePlaceholder(template?: FilterTemplate) {
    if (template) {
        if (template.placeholder) return template.placeholder
        if (filterRequiresCustomInput(template)) return `enter ${template.key}`
        if (template.operator === 'after' || template.operator === 'before') return 'YYYY-MM-DD HH:MM:SS'
    }
    return 'select value'
}

function filterRequiresCustomInput(template: FilterTemplate): boolean {
    // no predefined values to select from
    return !template.possibleValues && !template.possibleValuesSync
}

function formattedOperator(op: FilterBy['operator']): string {
    return op === 'isOneOf' ? 'is one of' : op
}

function toSavedFilter(f: FilterBy): SavedFilter[] {
    if ('value' in f) {
        return [{ key: f.key, operator: f.operator, value: { value: f.value, displayName: f.displayName ?? f.value } }]
    }
    return f.values.map((childFilter) => ({
        key: f.key,
        operator: f.operator,
        value: {
            value: childFilter.value,
            displayName: childFilter.displayName,
        },
    }))
}
function toFilterBy(filters: SavedFilter[]): FilterBy[] {
    const groupedByKeyOperator: { [key: string]: FilterBy } = {}
    for (const { key, operator, value } of filters) {
        const groupKey = `${key}-${operator}`
        const existingFilter: FilterBy | undefined = groupedByKeyOperator[groupKey]
        if (!existingFilter) {
            groupedByKeyOperator[groupKey] =
                operator === 'isOneOf'
                    ? { key, operator, values: [value] }
                    : {
                          key,
                          operator,
                          value: value.value,
                          displayName: value.displayName,
                      }
        } else if (existingFilter.operator === 'isOneOf') {
            existingFilter.values.push(value)
        }
    }
    return Object.values(groupedByKeyOperator)
}

const FilterView = ({
    filter: { key, operator, value },
    onDelete,
    dataTestId,
}: {
    filter: SavedFilter | WipFilter
    onDelete: () => void
    dataTestId: string
}) => {
    const isWip = !value
    return (
        <Chip
            sx={{ p: 0.5, m: 0.5, height: '100%' }}
            label={`${key} ${operator ? formattedOperator(operator) : ''} ${value?.displayName || ''}`}
            data-testid={dataTestId}
            onDelete={onDelete}
            size="small"
            variant={isWip ? 'outlined' : 'filled'}
        />
    )
}

const Root = styled('div')(({ theme }) => ({
    borderRadius: 800,
    flex: 1,
    minWidth: 0,
    backgroundColor:
        theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.12)
            : alpha(theme.palette.text.primary, 0.04),
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.text.primary, 0.23)}`,
    transition: theme.transitions.create('box-shadow', { duration: theme.transitions.duration.shortest }),
    '&:hover': {
        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.text.primary, 0.45)}`,
    },
    '&:focus-within': {
        boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
    },
}))

const Input = styled('input')(({ theme }) => ({
    height: 40,
    font: 'inherit',
    color: theme.palette.text.primary,
    marginLeft: 10,
    border: 0,
    outline: 0,
    flexGrow: 1,
    flexShrink: 0,
    minWidth: 120,
    textOverflow: 'ellipsis',
    backgroundColor: 'transparent',
    '::placeholder': {
        fontFamily: theme.typography.body1.fontFamily,
        fontSize: theme.typography.body1.fontSize,
        color: theme.palette.text.disabled,
    },
}))

const OptionsContainer = styled('ul')(({ theme }) => ({
    minWidth: 200,
    maxHeight: 320,
    overflowY: 'auto',
    listStyle: 'none',
    paddingLeft: 0,
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,

    '& li': {
        padding: 6,
    },
    '& li[aria-selected="true"]': {
        backgroundColor: theme.palette.action.selected,
    },
    [`& li.${autocompleteClasses.focused}`]: {
        backgroundColor: theme.palette.action.focus,
        cursor: 'pointer',
    },
}))
